import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Switch,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Alert,
  CircularProgress,
  IconButton,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  NetworkCheck as NetworkIcon,
  PlayCircleOutline as PlayCircleOutlineIcon
} from '@mui/icons-material';
import api from '../../services/api';
import { AttackType, Scenario, ScenarioCreateRequest, ScenarioUpdateRequest } from '../../types/attacks';
import TargetValidation, { TargetValidationRef } from '../TargetValidation';
import AttackConfigForm from '../AttackConfigForm';
import TestRunDialog, { TestRunRequestParams } from './TestRunDialog';
import { useTenant } from '../../contexts/TenantContext';
import { normalizeConfigurationForForm } from '../../utils/attackConfig';

interface CreateScenarioDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError?: (message: string) => void;
  mode?: 'create' | 'edit';
  scenarioToEdit?: Scenario | null;
  initialTarget?: string;
  initialResearch?: { attackTypeId: string; name: string; description: string };
}

const isCidr = (value: string): boolean => /^[\d.a-fA-F:]+\/\d{1,3}$/.test(value.trim());
const CATEGORY_ORDER = ['volume', 'infra', 'application'];

type BandwidthUnit = 'bps' | 'Kbps' | 'Mbps' | 'Gbps';
const BANDWIDTH_UNIT_MULTIPLIERS: Record<BandwidthUnit, number> = {
  bps: 1,
  Kbps: 1_000,
  Mbps: 1_000_000,
  Gbps: 1_000_000_000,
};

/** Pick the largest unit that keeps the displayed value >= 1 (falls back to bps). */
const bpsToDisplay = (bps: number | null | undefined): { value: string; unit: BandwidthUnit } => {
  if (bps === null || bps === undefined || !Number.isFinite(bps)) return { value: '', unit: 'Mbps' };
  const units: BandwidthUnit[] = ['Gbps', 'Mbps', 'Kbps', 'bps'];
  for (const unit of units) {
    const scaled = bps / BANDWIDTH_UNIT_MULTIPLIERS[unit];
    if (scaled >= 1) return { value: String(scaled), unit };
  }
  return { value: String(bps), unit: 'bps' };
};

const formatAggregate = (value: number, suffix: string): string => {
  const rounded = value >= 100 ? Math.round(value) : Math.round(value * 100) / 100;
  return `${rounded.toLocaleString()} ${suffix}`;
};

/** Pick the largest bandwidth unit that keeps the aggregate value readable. */
const formatBandwidthAggregate = (totalBps: number): string => {
  const units: BandwidthUnit[] = ['Gbps', 'Mbps', 'Kbps', 'bps'];
  for (const unit of units) {
    const scaled = totalBps / BANDWIDTH_UNIT_MULTIPLIERS[unit];
    if (scaled >= 1) return formatAggregate(scaled, unit);
  }
  return formatAggregate(totalBps, 'bps');
};

const CreateScenarioDialog: React.FC<CreateScenarioDialogProps> = ({
  open,
  onClose,
  onSuccess,
  onError,
  mode = 'create',
  scenarioToEdit = null,
  initialTarget,
  initialResearch
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { selectedTenant } = useTenant();
  const [step, setStep] = useState(1);
  const [attackTypes, setAttackTypes] = useState<AttackType[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [form, setForm] = useState<ScenarioCreateRequest>({
    name: '',
    description: '',
    attack_type_id: '',
    configuration: { targets: [''], threads: 10 }
  });

  // Resource caps (CAO-202) are per-bot and enforced by the bot runtime; each
  // is edited as free text so the field can be empty (= unlimited) without
  // fighting a numeric input. `plannedBotCount` is local-only UI state used
  // solely to preview the effective aggregate across bots — it is not part of
  // the scenario and is never sent to the API.
  const [maxRpsText, setMaxRpsText] = useState('');
  const [maxBandwidthText, setMaxBandwidthText] = useState('');
  const [maxBandwidthUnit, setMaxBandwidthUnit] = useState<BandwidthUnit>('Mbps');
  const [maxPpsText, setMaxPpsText] = useState('');
  const [plannedBotCount, setPlannedBotCount] = useState('1');

  const targetValidationRef = useRef<TargetValidationRef>(null);
  const [targetValidation, setTargetValidation] = useState({ allValid: true, invalidTargets: [] as string[] });

  // Test Run (CAO-156): run the in-progress configuration once, before saving.
  const [testRunOpen, setTestRunOpen] = useState(false);
  const [testRunRequest, setTestRunRequest] = useState<TestRunRequestParams | null>(null);

  const getAttackClassIcon = (className: string) => {
    switch (className.toLowerCase()) {
      case 'volumetric':
        return <SpeedIcon sx={{ color: theme.palette.warning.main }} />;
      case 'infrastructure':
        return <NetworkIcon sx={{ color: theme.palette.secondary.main }} />;
      case 'application':
        return <SecurityIcon sx={{ color: theme.palette.primary.main }} />;
      default:
        return <SecurityIcon sx={{ color: theme.palette.info.main }} />;
    }
  };

  const getDefaultConfiguration = (attackType: AttackType) => {
    const schema: any = attackType?.config_schema;
    if (!schema || typeof schema !== 'object') return { targets: [''], threads: 10 };

    const required = schema.required || [];

    const extractDefaults = (node: any): any => {
      if (!node || typeof node !== 'object') return undefined;
      if (Object.prototype.hasOwnProperty.call(node, 'default')) return node.default;

      const nodeType = node.type;
      if (nodeType === 'object') {
        const props = node.properties || {};
        const result: Record<string, any> = {};
        Object.entries(props).forEach(([key, prop]: [string, any]) => {
          const val = extractDefaults(prop);
          if (val !== undefined) {
            result[key] = val;
          } else if (prop?.type === 'array') {
            result[key] = required.includes(key) ? [''] : [];
          } else if (prop?.type === 'object') {
            result[key] = {};
          }
        });
        return result;
      }

      if (nodeType === 'array') return node.default !== undefined ? node.default : [];
      return node.default;
    };

    const defaults = extractDefaults(schema) || {};
    
    // Ensure targets and threads are always present
    // Some schemas default targets to [] which makes the UI render 0 inputs; always keep at least one row.
    if (!Array.isArray(defaults.targets) || defaults.targets.length === 0) defaults.targets = [''];
    if (typeof defaults.threads !== 'number' || defaults.threads <= 0) defaults.threads = 10;
    
    return defaults;
  };

  const getAttackCategory = (attackClass: string) => {
    const normalized = attackClass.toLowerCase();
    if (normalized.includes('volum')) return 'volume';
    if (normalized.includes('infra')) return 'infra';
    if (normalized.includes('app')) return 'application';
    return normalized;
  };

  const groupedAttackTypes = useMemo(() => {
    const grouped = new Map<string, AttackType[]>();

    attackTypes.forEach((type) => {
      const category = getAttackCategory(type.attack_class);
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)?.push(type);
    });

    const orderedCategories = [
      ...CATEGORY_ORDER.filter((category) => grouped.has(category)),
      ...Array.from(grouped.keys())
        .filter((category) => !CATEGORY_ORDER.includes(category))
        .sort((a, b) => a.localeCompare(b))
    ];

    return orderedCategories.map((category) => ({
      category,
      types: (grouped.get(category) || []).sort((a, b) => a.name.localeCompare(b.name))
    }));
  }, [attackTypes]);

  // Load attack types
  useEffect(() => {
    if (open) {
      loadAttackTypes();
    }
  }, [open]);

  // Prefill when editing
  useEffect(() => {
    if (!open) return;
    if (mode !== 'edit' || !scenarioToEdit) return;

    setStep(1);
    setForm({
      name: scenarioToEdit.name || '',
      description: scenarioToEdit.description || '',
      attack_type_id: scenarioToEdit.attack_type_id,
      // A stored configuration can be either shape -- UI-shaped with a stale
      // bot-side ssl_options dict from a pre-CAO-238 save, or bot-shaped
      // outright, which is what the JSON editor and API/MCP callers can post.
      // Fold it back onto the form's fields either way, or the SSL toggles open
      // on their schema defaults and the next save writes those over what was
      // stored.
      configuration: scenarioToEdit.configuration
        ? normalizeConfigurationForForm(
            scenarioToEdit.configuration as Record<string, any>,
            scenarioToEdit.attack_type_id
          )
        : { targets: [''], threads: 10 }
    });
    setMaxRpsText(scenarioToEdit.max_rps != null ? String(scenarioToEdit.max_rps) : '');
    setMaxPpsText(scenarioToEdit.max_pps != null ? String(scenarioToEdit.max_pps) : '');
    const bandwidthDisplay = bpsToDisplay(scenarioToEdit.max_bandwidth_bps);
    setMaxBandwidthText(bandwidthDisplay.value);
    setMaxBandwidthUnit(bandwidthDisplay.unit);
  }, [open, mode, scenarioToEdit]);

  // Optional prefill for create flow (e.g. from Assets page deep-link)
  useEffect(() => {
    if (!open) return;
    if (mode !== 'create') return;
    const target = String(initialTarget || '').trim();
    if (!target) return;

    setStep(1);
    setForm({
      name: '',
      description: '',
      attack_type_id: '',
      configuration: {
        targets: [target],
        threads: 10,
        expand_per_target: [true],
      },
    });
    setMaxRpsText('');
    setMaxBandwidthText('');
    setMaxBandwidthUnit('Mbps');
    setMaxPpsText('');
    setPlannedBotCount('1');
  }, [open, mode, initialTarget]);

  // Apply catalog defaults once per research draft after types are loaded.
  const appliedResearch = useRef<object | null>(null);
  useEffect(() => {
    if (!open) {
      appliedResearch.current = null;
      return;
    }
    if (mode !== 'create' || !initialResearch || appliedResearch.current === initialResearch) return;
    const attack = attackTypes.find(type => type.id === initialResearch.attackTypeId);
    if (!attack) return;
    appliedResearch.current = initialResearch;
    setForm({
      name: initialResearch.name,
      description: initialResearch.description,
      attack_type_id: attack.id,
      configuration: { ...getDefaultConfiguration(attack), targets: [initialTarget || ''] },
    });
  }, [open, mode, initialResearch, initialTarget, attackTypes]);

  const loadAttackTypes = async () => {
    try {
      setLoading(true);
      const types = await api.getAttackTypes();
      setAttackTypes(types);
    } catch (error) {
      console.error('Error loading attack types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setForm({ name: '', description: '', attack_type_id: '', configuration: { targets: [''], threads: 10 } });
    setMaxRpsText('');
    setMaxBandwidthText('');
    setMaxBandwidthUnit('Mbps');
    setMaxPpsText('');
    setPlannedBotCount('1');
    onClose();
  };

  const handleNext = async () => {
    if (step === 1 && form.attack_type_id) {
      // Validate targets before proceeding
      const targets = (form.configuration as any).targets?.filter((t: string) => t.trim()) || [];
      if (targets.length === 0) {
        return;
      }
      
      // Trigger target validation
      targetValidationRef.current?.validate();
      setStep(2);
    } else if (step === 2) {
      if (mode === 'create' && !initialResearch) {
        // Generate scenario name from attack type and targets
        const attackType = attackTypes.find(t => t.id === form.attack_type_id);
        const targets = (form.configuration as any).targets?.filter((t: string) => t.trim()) || [];
        const firstTarget = targets[0] || 'Target';
        const targetDisplay = firstTarget.length > 30 ? firstTarget.substring(0, 30) + '...' : firstTarget;
        const generatedName = `${attackType?.name || 'Attack'} - ${targetDisplay}`;
        setForm(prev => ({ ...prev, name: generatedName }));
      }
      setStep(3);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const capFieldError = (text: string): string | null => {
    const trimmed = text.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed <= 0) return 'Must be a positive number, or blank for unlimited';
    return null;
  };

  const capValueOrNull = (text: string): number | null => {
    const trimmed = text.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  /** Filter out empty targets and keep per-target metadata aligned; shared by
   * Create/Save and Test Run so both send the same shape to the backend. */
  const buildCleanedConfiguration = (): any => {
    const rawTargets: string[] = (form.configuration as any).targets || [];
    const rawExpandFlags: boolean[] = (form.configuration as any).expand_per_target || [];

    const cleanedTargets: string[] = [];
    const cleanedExpandFlags: boolean[] = [];
    rawTargets.forEach((t: string, i: number) => {
      const trimmed = String(t ?? '').trim();
      if (!trimmed) {
        return;
      }
      cleanedTargets.push(trimmed);
      // Default is expand=true when missing.
      cleanedExpandFlags.push(rawExpandFlags[i] !== false);
    });

    return {
      ...form.configuration,
      targets: cleanedTargets,
      // Keep per-target metadata aligned with targets for all attack types.
      expand_per_target: cleanedExpandFlags,
    };
  };

  // Resource caps (CAO-202): per-bot, optional. Bandwidth is normalized to
  // bits/second before it leaves the browser; the unit selector is purely a
  // display/input convenience. Shared by Create/Save and Test Run.
  const resolveResourceCaps = () => {
    const maxRpsValue = capValueOrNull(maxRpsText);
    const maxPpsValue = capValueOrNull(maxPpsText);
    const maxBandwidthValue = capValueOrNull(maxBandwidthText);
    const maxBandwidthBps = maxBandwidthValue !== null
      ? maxBandwidthValue * BANDWIDTH_UNIT_MULTIPLIERS[maxBandwidthUnit]
      : null;
    return { maxRpsValue, maxPpsValue, maxBandwidthBps };
  };

  const handleTestRun = () => {
    if (!selectedAttackType) return;
    const { maxRpsValue, maxBandwidthBps, maxPpsValue } = resolveResourceCaps();
    setTestRunRequest({
      attack_type_id: selectedAttackType.id,
      attack_type_name: selectedAttackType.name,
      configuration: buildCleanedConfiguration(),
      max_rps: maxRpsValue,
      max_bandwidth_bps: maxBandwidthBps,
      max_pps: maxPpsValue,
    });
    setTestRunOpen(true);
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.attack_type_id) {
      return;
    }
    if (capFieldError(maxRpsText) || capFieldError(maxBandwidthText) || capFieldError(maxPpsText)) {
      if (onError) onError('Resource caps must be positive numbers, or left blank for unlimited');
      return;
    }

    try {
      setCreating(true);

      const cleanedConfig = buildCleanedConfiguration();

      const { maxRpsValue, maxPpsValue, maxBandwidthBps } = resolveResourceCaps();

      if (mode === 'edit' && scenarioToEdit?.id) {
        const updateRequest: ScenarioUpdateRequest = {
          name: form.name.trim(),
          description: form.description?.trim() || undefined,
          configuration: cleanedConfig
        };
        if (maxRpsValue !== null) {
          updateRequest.max_rps = maxRpsValue;
        } else if (scenarioToEdit.max_rps != null) {
          updateRequest.clear_max_rps = true;
        }
        if (maxBandwidthBps !== null) {
          updateRequest.max_bandwidth_bps = maxBandwidthBps;
        } else if (scenarioToEdit.max_bandwidth_bps != null) {
          updateRequest.clear_max_bandwidth_bps = true;
        }
        if (maxPpsValue !== null) {
          updateRequest.max_pps = maxPpsValue;
        } else if (scenarioToEdit.max_pps != null) {
          updateRequest.clear_max_pps = true;
        }
        await api.updateScenario(scenarioToEdit.id, updateRequest);
      } else {
        await api.createScenario({
          name: form.name.trim(),
          description: form.description?.trim() || undefined,
          attack_type_id: form.attack_type_id,
          configuration: cleanedConfig,
          max_rps: maxRpsValue,
          max_bandwidth_bps: maxBandwidthBps,
          max_pps: maxPpsValue,
        });
      }
      
      handleClose();
      onSuccess();
    } catch (error: any) {
      console.error('Error creating scenario:', error);
      const fallbackMsg = mode === 'edit' ? 'Failed to update scenario' : 'Failed to create scenario';
      const detail = error.response?.data?.detail ?? error.message ?? fallbackMsg;
      const errorMessage = typeof detail === 'string' ? detail : (detail?.message || detail?.msg || (() => {
        try { return JSON.stringify(detail); } catch { return fallbackMsg; }
      })());
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setCreating(false);
    }
  };

  const canProceed = () => {
    if (step === 1) {
      const targets = (form.configuration as any).targets?.filter((t: string) => t.trim()) || [];
      return form.attack_type_id && targets.length > 0;
    }
    if (step === 2) {
      return true; // Config is optional, can proceed
    }
    if (step === 3) {
      return (
        form.name.trim() !== '' &&
        !capFieldError(maxRpsText) &&
        !capFieldError(maxBandwidthText) &&
        !capFieldError(maxPpsText)
      );
    }
    return false;
  };

  const selectedAttackType = attackTypes.find(t => t.id === form.attack_type_id);
  const targetsLocked = mode === 'edit' && (scenarioToEdit?.execution_count ?? 0) > 0;

  const updateTargets = (index: number, value: string) => {
    const targets = [...((form.configuration as any).targets || [''])];
    targets[index] = value;
    setForm(prev => ({
      ...prev,
      configuration: { ...(prev.configuration as any), targets }
    }));
  };

  const addTarget = () => {
    const targets = [...((form.configuration as any).targets || []), ''];
    const expand_per_target = [...((form.configuration as any).expand_per_target || [])];
    expand_per_target.push(true);
    setForm(prev => ({
      ...prev,
      configuration: { ...(prev.configuration as any), targets, expand_per_target }
    }));
  };

  const insertTargetAfter = (index: number) => {
    const targets = [...((form.configuration as any).targets || [''])];
    const expand_per_target = [...((form.configuration as any).expand_per_target || [])];
    targets.splice(index + 1, 0, '');
    expand_per_target.splice(index + 1, 0, true);
    setForm(prev => ({
      ...prev,
      configuration: { ...(prev.configuration as any), targets, expand_per_target }
    }));
  };

  const removeTarget = (index: number) => {
    const targets = ((form.configuration as any).targets || []).filter((_: any, i: number) => i !== index);
    const expand_per_target = ((form.configuration as any).expand_per_target || []).filter((_: any, i: number) => i !== index);
    setForm(prev => ({
      ...prev,
      configuration: { ...(prev.configuration as any), targets, expand_per_target }
    }));
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx:{
          backgroundColor: (theme) => theme.palette.background.paper,
          backdropFilter: 'blur(20px)',
          border: `2px solid ${alpha(theme.palette.primary.main, isDark ? 0.3 : 0.25)}`,
          boxShadow: `0 0 40px ${alpha(theme.palette.primary.main, 0.2)}`,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 700,
                color: theme.palette.primary.main,
                textShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                mb: 0.5,
              }}
            >
              {mode === 'edit' ? 'EDIT SCENARIO' : 'CREATE NEW SCENARIO'}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontFamily: '"Space Mono", monospace',
                color: 'text.secondary',
                fontSize: '0.95rem',
              }}
            >
              {step === 1 && 'Step 1: Select Attack Type & Targets'}
              {step === 2 && 'Step 2: Configure Parameters'}
              {step === 3 && 'Step 3: Name Your Scenario'}
            </Typography>
          </Box>
          <Box
            sx={{
              px: 2,
              py: 1,
              borderRadius: 2,
              background: alpha(theme.palette.primary.main, 0.1),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                color: theme.palette.primary.main,
                fontWeight: 700,
              }}
            >
              {step} / 3
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* Step 1: Attack Type and Targets */}
          {step === 1 && (
            <>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Attack Type</InputLabel>
                <Select
                  value={form.attack_type_id}
                  label="Attack Type"
                  disabled={mode === 'edit'}
                  onChange={(e) => {
                    const selectedType = attackTypes.find(t => t.id === e.target.value);
                    const defaultConfig = selectedType ? getDefaultConfiguration(selectedType) : { targets: [''], threads: 10 };

                    // Preserve targets and per-target metadata that the user already entered/pre-filled.
                    const existingTargets = (form.configuration as any).targets;
                    const existingExpandPerTarget = (form.configuration as any).expand_per_target;
                    const hasExistingTargets = Array.isArray(existingTargets) && existingTargets.some((t: string) => t.trim());

                    const mergedConfig = {
                      ...defaultConfig,
                      ...(hasExistingTargets ? { targets: existingTargets } : {}),
                      ...(hasExistingTargets && existingExpandPerTarget ? { expand_per_target: existingExpandPerTarget } : {}),
                    };

                    setForm({ ...form, attack_type_id: e.target.value, configuration: mergedConfig });
                  }}
                >
                  {loading && (
                    <MenuItem disabled value="">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">Loading attack types…</Typography>
                      </Box>
                    </MenuItem>
                  )}
                  {groupedAttackTypes.flatMap((group) => [
                    <ListSubheader key={`header-${group.category}`} sx={{ fontFamily: '"Space Mono", monospace', textTransform: 'uppercase' }}>
                      {group.category}
                    </ListSubheader>,
                    ...group.types.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getAttackClassIcon(type.attack_class)}
                          <Box>
                            <Typography variant="body2">{type.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {group.category}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))
                  ])}
                </Select>
              </FormControl>

              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 1.5, 
                  fontFamily: '"Space Mono", monospace',
                  color: 'text.primary',
                  fontSize: '1rem',
                }}
              >
                TARGETS *
              </Typography>
              
              {targetsLocked ? (
                <Alert severity="warning" sx={{ mb: 2, fontSize: '0.9rem' }}>
                  Targets are <strong>locked</strong> — this scenario has prior executions and targets cannot be changed.
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2, fontSize: '0.9rem' }}>
                  Enter domains, IPs, or URLs. Use &lt;random_string&gt; for dynamic values.
                </Alert>
              )}
              
              {((form.configuration as any).targets || ['']).map((target: string, index: number) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                  <TextField
                    fullWidth
                    size="medium"
                    placeholder="example.com or 192.168.1.1 or https://api.example.com/path"
                    value={target}
                    disabled={targetsLocked}
                    onChange={(e) => updateTargets(index, e.target.value)}
                    onPaste={(e) => {
                      const text = e.clipboardData.getData('text');
                      const lines = text.split(/[\n\r]+/).map(l => l.trim()).filter(l => l);
                      if (lines.length > 1) {
                        e.preventDefault();
                        const targets = [...((form.configuration as any).targets || [''])];
                        const expandFlags = [...((form.configuration as any).expand_per_target || [])];
                        targets.splice(index, 1, ...lines);
                        expandFlags.splice(index, 1, ...lines.map(() => true));
                        setForm(prev => ({
                          ...prev,
                          configuration: { ...(prev.configuration as any), targets, expand_per_target: expandFlags }
                        }));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        insertTargetAfter(index);
                        setTimeout(() => {
                          const inputs = document.querySelectorAll<HTMLInputElement>('[data-target-input]');
                          const next = inputs[index + 1];
                          if (next) next.focus();
                        }, 50);
                      }
                    }}
                    inputProps={{ 'data-target-input': true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontFamily: '"Space Mono", monospace',
                        fontSize: '1rem',
                      },
                    }}
                  />
                  {isCidr(target) && (
                    <Tooltip title="When ON: expand CIDR to individual IPs (each IP is a separate target). When OFF: treat the whole CIDR as one target.">
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 70,
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        }}
                      >
                        <Switch
                          size="small"
                          disabled={targetsLocked}
                          checked={((form.configuration as any).expand_per_target || [])[index] !== false}
                          onChange={(e) => {
                            const arr = [...((form.configuration as any).expand_per_target || [])];
                            arr[index] = e.target.checked;
                            setForm(prev => ({
                              ...prev,
                              configuration: { ...(prev.configuration as any), expand_per_target: arr }
                            }));
                          }}
                          sx={{ mb: 0 }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: '"Space Mono", monospace',
                            fontSize: '0.6rem',
                            color: 'text.secondary',
                            lineHeight: 1,
                            textAlign: 'center',
                          }}
                        >
                          {((form.configuration as any).expand_per_target || [])[index] !== false ? 'EXPAND' : 'WHOLE'}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                  {!targetsLocked && ((form.configuration as any).targets?.length || 0) > 1 && (
                    <IconButton 
                      onClick={() => removeTarget(index)}
                      sx={{
                        color: theme.palette.error.main,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.error.main, 0.1),
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                  {!targetsLocked && index === ((form.configuration as any).targets?.length || 1) - 1 && (
                    <IconButton 
                      onClick={addTarget}
                      sx={{
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        },
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
              

              {/* Target Validation */}
              {form.attack_type_id && (
                <Box sx={{ mt: 2 }}>
                  <TargetValidation
                    ref={targetValidationRef}
                    targets={((form.configuration as any).targets || []).filter((t: string) => t.trim())}
                    validateOnChange={false}
                    onTargetsValidated={(allValid: boolean) => {
                      setTargetValidation(prev => ({ ...prev, allValid }));
                    }}
                    onValidationResults={(results: any[]) => {
                      const invalidTargets = results
                        .filter((r: any) => !r.is_approved)
                        .map((r: any) => r.target);
                      setTargetValidation({
                        invalidTargets,
                        allValid: invalidTargets.length === 0
                      });
                    }}
                    onRequestTargets={async (targets: string[]) => {
                      if (!selectedTenant?.tenant_id) {
                        throw new Error('No tenant selected');
                      }
                      
                      const promises = targets.map(target => 
                        api.createAllowListEntry(selectedTenant.tenant_id, {
                          value: target,
                          reason: 'Requested from scenario creation'
                        })
                      );
                      
                      await Promise.all(promises);
                    }}
                  />
                </Box>
              )}
            </>
          )}

          {/* Step 2: Configuration */}
          {step === 2 && selectedAttackType && (
            <AttackConfigForm
              attackType={selectedAttackType}
              configuration={form.configuration}
              onChange={(newConfig) => {
                setForm(prev => ({ ...prev, configuration: newConfig }));
              }}
              isEditMode={false}
              restrictedFields={[]}
            />
          )}

          {/* Step 3: Name */}
          {step === 3 && (
            <>
              <TextField
                autoFocus
                fullWidth
                label="Scenario Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    fontFamily: '"Space Mono", monospace',
                  },
                }}
                helperText={mode === 'edit' ? 'Edit the scenario name' : 'A name has been suggested based on your configuration, but you can customize it'}
              />
              
              <TextField
                fullWidth
                label="Description (Optional)"
                multiline
                rows={3}
                value={form.description || ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: '"Space Mono", monospace',
                  },
                }}
                helperText="Add any additional details about this attack scenario"
              />

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  background: alpha(theme.palette.warning.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontFamily: '"JetBrains Mono", monospace', color: 'text.primary' }}>
                  RESOURCE CAPS (per-bot, optional)
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary' }}>
                  Enforced by the bot runtime — each bot independently honors these limits. Leave blank for unlimited (current behavior).
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label="Max RPS"
                    placeholder="Unlimited"
                    value={maxRpsText}
                    onChange={(e) => setMaxRpsText(e.target.value)}
                    error={!!capFieldError(maxRpsText)}
                    helperText={capFieldError(maxRpsText) || 'Max requests/second'}
                    sx={{ flex: '1 1 160px', '& .MuiOutlinedInput-root': { fontFamily: '"Space Mono", monospace' } }}
                  />

                  <Box sx={{ display: 'flex', gap: 1, flex: '1 1 260px' }}>
                    <TextField
                      label="Max Bandwidth"
                      placeholder="Unlimited"
                      value={maxBandwidthText}
                      onChange={(e) => setMaxBandwidthText(e.target.value)}
                      error={!!capFieldError(maxBandwidthText)}
                      helperText={capFieldError(maxBandwidthText) || 'Max byte-rate'}
                      sx={{ flex: 1, '& .MuiOutlinedInput-root': { fontFamily: '"Space Mono", monospace' } }}
                    />
                    <FormControl sx={{ minWidth: 90 }}>
                      <InputLabel>Unit</InputLabel>
                      <Select
                        label="Unit"
                        value={maxBandwidthUnit}
                        onChange={(e) => setMaxBandwidthUnit(e.target.value as BandwidthUnit)}
                      >
                        <MenuItem value="bps">bps</MenuItem>
                        <MenuItem value="Kbps">Kbps</MenuItem>
                        <MenuItem value="Mbps">Mbps</MenuItem>
                        <MenuItem value="Gbps">Gbps</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <TextField
                    label="Max PPS"
                    placeholder="Unlimited"
                    value={maxPpsText}
                    onChange={(e) => setMaxPpsText(e.target.value)}
                    error={!!capFieldError(maxPpsText)}
                    helperText={capFieldError(maxPpsText) || 'Max packets/second (raw-socket attacks)'}
                    sx={{ flex: '1 1 160px', '& .MuiOutlinedInput-root': { fontFamily: '"Space Mono", monospace' } }}
                  />
                </Box>

                {(maxRpsText.trim() || maxBandwidthText.trim() || maxPpsText.trim()) && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: `1px dashed ${alpha(theme.palette.warning.main, 0.3)}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Effective aggregate for
                      </Typography>
                      <TextField
                        size="small"
                        value={plannedBotCount}
                        onChange={(e) => setPlannedBotCount(e.target.value.replace(/[^0-9]/g, ''))}
                        sx={{ width: 70, '& .MuiOutlinedInput-input': { py: 0.5, textAlign: 'center', fontFamily: '"Space Mono", monospace' } }}
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        planned bot(s) — total load is roughly (per-bot cap × active bots):
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1, fontFamily: '"Space Mono", monospace', color: theme.palette.warning.main }}>
                      {[
                        (() => {
                          const v = capValueOrNull(maxRpsText);
                          const bots = Math.max(1, parseInt(plannedBotCount, 10) || 1);
                          return v !== null ? formatAggregate(v * bots, 'req/s') : null;
                        })(),
                        (() => {
                          const v = capValueOrNull(maxBandwidthText);
                          const bots = Math.max(1, parseInt(plannedBotCount, 10) || 1);
                          return v !== null ? formatBandwidthAggregate(v * BANDWIDTH_UNIT_MULTIPLIERS[maxBandwidthUnit] * bots) : null;
                        })(),
                        (() => {
                          const v = capValueOrNull(maxPpsText);
                          const bots = Math.max(1, parseInt(plannedBotCount, 10) || 1);
                          return v !== null ? formatAggregate(v * bots, 'pps') : null;
                        })(),
                      ].filter(Boolean).join('  •  ') || '—'}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  background: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, fontFamily: '"JetBrains Mono", monospace', color: 'text.primary' }}>
                  Configuration Summary
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: '"Space Mono", monospace', lineHeight: 1.8 }}>
                  <strong>Attack Type:</strong> {selectedAttackType?.name}<br/>
                  <strong>Targets:</strong> {((form.configuration as any).targets?.filter((t: string) => t.trim()) || []).join(', ')}<br/>
                  <strong>Threads:</strong> {(form.configuration as any).threads || 10}<br/>
                  <strong>Parameters:</strong> {Object.keys(form.configuration).length} configured
                </Typography>
              </Box>

              {/* Warning if targets are not in allow list */}
              {!targetValidation.allValid && targetValidation.invalidTargets.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Some targets are not in the allow list
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    The following targets need approval:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mb: 1 }}>
                    {targetValidation.invalidTargets.map((target, idx) => (
                      <li key={idx}>
                        <Typography variant="body2" sx={{ fontFamily: '"Space Mono", monospace' }}>{target}</Typography>
                      </li>
                    ))}
                  </Box>
                  <Typography variant="body2">
                    <strong>Note:</strong> You can still create this scenario, but it cannot be activated until these targets are approved.
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, gap: 1 }}>
        <Button
          onClick={handleClose}
          size="large"
          sx={{
            fontFamily: '"Space Mono", monospace',
            fontWeight: 600,
            px: 3,
          }}
        >
          Cancel
        </Button>
        
        {step > 1 && (
          <Button
            onClick={handleBack}
            size="large"
            sx={{
              fontFamily: '"Space Mono", monospace',
              fontWeight: 600,
              px: 3,
            }}
          >
            Back
          </Button>
        )}

        {step >= 2 && selectedAttackType && (
          <Button
            onClick={handleTestRun}
            size="large"
            startIcon={<PlayCircleOutlineIcon />}
            sx={{
              fontFamily: '"Space Mono", monospace',
              fontWeight: 600,
              px: 3,
            }}
          >
            Test Run
          </Button>
        )}

        <Box sx={{ flexGrow: 1 }} />
        
        {step < 3 ? (
          <Button
            onClick={handleNext}
            variant="contained"
            size="large"
            disabled={!canProceed()}
            sx={{
              fontFamily: '"Space Mono", monospace',
              fontWeight: 600,
              px: 4,
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleCreate}
            variant="contained"
            size="large"
            disabled={!canProceed() || creating}
            startIcon={creating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : null}
            sx={{
              fontFamily: '"Space Mono", monospace',
              fontWeight: 600,
              px: 4,
            }}
          >
            {creating ? (mode === 'edit' ? 'Saving...' : 'Creating...') : (mode === 'edit' ? 'Save Changes' : 'Create Scenario')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
    <TestRunDialog
      open={testRunOpen}
      request={testRunRequest}
      onClose={() => {
        setTestRunOpen(false);
        setTestRunRequest(null);
      }}
    />
    </>
  );
};

export default CreateScenarioDialog;

